<script setup>
/**
 * Two logos, because the sidebar has two widths (XN-4507).
 *
 * A horizontal wordmark is ~4:1 and cannot survive the collapsed rail: scaled
 * to fit it is unreadable, cropped it is half a word. So the agency supplies
 * both — the wordmark for wide surfaces, a square mark for the rail — and
 * the card shows how each width falls back when one is missing.
 *
 * Everything the owner does here is staged in the parent's form; nothing is
 * written until the page's Save. Cancelling a crop therefore leaves the
 * previous logo exactly where it was, which is what "nothing partially saved"
 * has to mean. The uploads themselves do happen on crop-save (a media item
 * for the original and one for the result), so that a saved crop can be
 * resumed later from the same source.
 */
import axios from 'axios';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import LogoCropModal from './LogoCropModal.vue';
import { SLOT_SHAPES } from './logoSlots';
import { ensureViewBox } from '../../helpers/svgViewBox';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlModal from '../xl/XlModal.vue';

const props = defineProps({
  // { logo, logoMark, logoCrop, logoMarkCrop } — URLs, and JSON strings for
  // the crops, which is the shape the settings endpoint stores.
  modelValue: { type: Object, required: true },
  // The same shape as it was last saved. What the page has saved is the
  // server's to tidy; what it has not is this card's.
  savedValue: { type: Object, default: null },
  hubName: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const MAX_BYTES = 2 * 1024 * 1024;
// SVG or PNG (XN-4502 AC, XN-4579): what the design promises and the
// server enforces. A JPG never survived the crop as anything but a PNG anyway.
const ACCEPT = ['image/svg+xml', 'image/png'];

const SLOTS = [
  {
    key: 'logo', cropKey: 'logoCrop', shape: 'wide',
    title: 'Full logo',
    sub: 'Shown when the sidebar is open.',
    spec: 'SVG or PNG · horizontal, around 184 × 48 · up to 2 MB',
    // What stands in when this one is removed (AC2 names it).
    fallback: 'Wide surfaces show the hub name on its own.',
  },
  {
    key: 'logoMark', cropKey: 'logoMarkCrop', shape: 'square',
    title: 'Collapsed logo',
    sub: 'Shown when the sidebar is narrow. A square mark, an icon, or a monogram.',
    spec: 'SVG or PNG · square, at least 96 × 96 · up to 2 MB',
    fallback: 'The collapsed rail shows the full logo scaled down, or the first letter of the hub name.',
  },
];

const value = (key) => props.modelValue[key] || '';
const has = (key) => Boolean(value(key));
const initial = computed(() => (props.hubName || 'H').trim().charAt(0).toUpperCase() || 'H');

// One sentence that says which of the four states the sidebar is in. Same
// four the prototype's logoCard() draws, same wording.
const status = computed(() => {
  const full = has('logo');
  const mark = has('logoMark');
  if (full && mark) return { warn: false, text: 'Both supplied — each width uses the file made for it.' };
  if (full) return { warn: true, text: 'No collapsed logo yet, so the full one is scaled down to fit 54px and becomes hard to read. A square mark would fix that.' };
  if (mark) return { warn: false, text: 'Only the square mark is set, so the open sidebar shows the hub name on its own.' };
  return { warn: false, text: 'No logos yet. The sidebar shows your hub name on its own, and the collapsed rail its first letter.' };
});

function patch(changes) {
  emit('update:modelValue', { ...props.modelValue, ...changes });
}

/* ---- picking a file ---- */
const pickers = ref({});
const busy = ref(null); // slot key while uploading

function pick(slot) {
  pickers.value[slot.key]?.click();
}

function readAsDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read that file'));
    reader.readAsDataURL(blob);
  });
}

async function onPicked(slot, event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  if (!ACCEPT.includes(file.type)) {
    window.loadNotification?.({ message: 'That needs to be an SVG or PNG.', error: true });
    return;
  }
  if (file.size > MAX_BYTES) {
    window.loadNotification?.({ message: 'That file is over 2 MB.', error: true });
    return;
  }

  // An SVG scales without loss, so the crop has nothing to offer it
  // (XN-4540): it is uploaded once and applied as-is, the way the asset
  // library has always handled one.
  if (file.type === 'image/svg+xml') {
    await applyAsIs(slot, await ensureViewBox(file));
    return;
  }

  let src;
  try {
    src = await readAsDataUrl(file);
  } catch (error) {
    window.loadNotification?.({ message: error.message, error: true });
    return;
  }
  openCrop(slot, { src, file, coordinates: null });
}

/* ---- the crop ---- */
const crop = ref(null); // { slot, src, file, coordinates }

function openCrop(slot, { src, file, coordinates }) {
  crop.value = { slot, src, file, coordinates };
}

function parseCrop(slot) {
  try {
    const parsed = JSON.parse(value(slot.cropKey) || 'null');
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

// Crop resumes the last crop. A legacy logo has no record and still gets the
// button (it asks for a re-upload); an SVG applied as-is has a record with no
// coordinates, and nothing to resume.
function cropOffered(slot) {
  const stored = parseCrop(slot);
  return !stored || Boolean(stored.coordinates);
}

/**
 * Crop on a filled slot resumes the last crop (AC5): the original upload and
 * its coordinates are kept for exactly this. A logo that predates this card
 * has no original recorded, so the current file is fetched instead and the
 * crop starts from its full extent; if that fetch is refused (a store on
 * another origin without CORS), re-uploading is the honest answer rather than
 * a canvas that throws at Save.
 */
async function recrop(slot) {
  const stored = parseCrop(slot);
  const sourceUrl = stored?.source || value(slot.key);

  // Busy for the fetch too: a Remove or Replace landing while the source
  // is still downloading would have this open a crop of a logo that is
  // no longer there, and saving it would bring that logo back.
  busy.value = slot.key;
  try {
    const response = await fetch(sourceUrl, { mode: 'cors' });
    if (!response.ok) throw new Error(`fetch ${response.status}`);
    const src = await readAsDataUrl(await response.blob());
    openCrop(slot, { src, file: null, coordinates: stored?.coordinates || null });
  } catch {
    // A saved crop that will not load is a network blip, not a legacy logo;
    // opening the picker would throw the saved crop away for nothing.
    if (stored?.source) {
      window.loadNotification?.({ message: 'Could not load the saved logo to crop it. Check your connection and try again.', error: true });
      return;
    }
    window.loadNotification?.({
      message: 'That logo was uploaded before cropping existed and cannot be reopened. Upload it again to crop it.',
      error: true,
    });
    pick(slot);
  } finally {
    busy.value = null;
  }
}

async function upload(blob, fileName) {
  const form = new FormData();
  form.append('driver', 'logo');
  form.append('type', 'logo');
  form.append('file', new File([blob], fileName, { type: blob.type || 'image/png' }));
  const { data } = await axios.post(`${window.store.event_backend_api}/media-items`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const url = data?.data?.guid;
  if (!url) throw new Error('The upload did not return a file');
  // The id travels in the crop record so the server can delete this item once
  // a later save has superseded it. Nothing is deleted from here: edits are
  // only staged, and a staged delete would take a live logo away from a change
  // the owner then discards.
  return { url, id: data.data.id ?? null };
}

async function deleteMedia(id) {
  if (!id) return;
  try {
    await axios.post(`${window.store.event_backend_api}/media-items/delete`, { id });
  } catch {
    // Best effort: an orphan is untidy, not a failure the owner needs to hear about.
  }
}

/* ---- uploads nobody has saved ----
   Every crop uploads two files the moment it is saved, and the page's Save
   may never come: crop again, discard, or leave. The server only ever tidies
   what a save superseded, so these would leak. Ids uploaded from here are
   held until a save takes ownership of them; until then they are only ever
   staged, so deleting one can never take a live logo away. */
const pending = new Set();

function idsIn(record) {
  try {
    const parsed = JSON.parse(record || 'null');
    return parsed ? [parsed.sourceId, parsed.renderedId].filter(Boolean) : [];
  } catch {
    return [];
  }
}

watch(() => [props.modelValue, props.savedValue], () => {
  const saved = new Set([...idsIn(props.savedValue?.logoCrop), ...idsIn(props.savedValue?.logoMarkCrop)]);
  const staged = new Set([...idsIn(props.modelValue.logoCrop), ...idsIn(props.modelValue.logoMarkCrop)]);
  for (const id of [...pending]) {
    if (saved.has(id)) pending.delete(id); // the server's now
    else if (!staged.has(id)) { pending.delete(id); deleteMedia(id); } // replaced or discarded before any save
  }
}, { deep: true, flush: 'sync' });

// An original that has landed while its render is still uploading sits in
// no record yet, so the watcher must not see it (it would read it as
// discarded) — but teardown must, or a render that never settles would
// leave it behind. Held here until the record is staged.
const inFlight = new Set();

// An upload that lands after the card is gone has nobody to hold it, so it
// is deleted on arrival and the second upload never starts.
let tornDown = false;
onBeforeUnmount(() => {
  tornDown = true;
  pending.forEach(deleteMedia);
  pending.clear();
  inFlight.forEach(deleteMedia);
  inFlight.clear();
});

async function onCropSaved({ blob, coordinates }) {
  // Vue does not await an event listener, so the modal's own loading flag
  // clears the moment it emits; a second click before the uploads land
  // would start them again. `busy` is set synchronously, so the second call
  // sees it and returns.
  if (busy.value) return;
  const { slot, file } = crop.value;
  busy.value = slot.key;

  // The original is only uploaded when a new file was picked; a re-crop
  // already has its source on record and resumes from it.
  const stored = parseCrop(slot);
  let source = { url: stored?.source || value(slot.key), id: stored?.sourceId ?? null };
  let uploadedOriginal = null;

  try {
    // Each id is held the moment its upload lands, so leaving the page
    // between the two uploads still tidies the first.
    // If the card is gone by the time an upload lands, what landed is
    // deleted on arrival; an original that landed earlier was drained at
    // teardown from inFlight.
    if (file) {
      uploadedOriginal = await upload(file, file.name);
      if (tornDown) { await deleteMedia(uploadedOriginal.id); return; }
      if (uploadedOriginal.id) inFlight.add(uploadedOriginal.id);
      source = uploadedOriginal;
    }
    const rendered = await upload(blob, `${slot.key}-${Date.now()}.png`);
    if (tornDown) { await deleteMedia(rendered.id); return; }
    if (uploadedOriginal?.id) {
      inFlight.delete(uploadedOriginal.id);
      pending.add(uploadedOriginal.id);
    }
    if (rendered.id) pending.add(rendered.id);

    patch({
      [slot.key]: rendered.url,
      [slot.cropKey]: JSON.stringify({
        source: source.url,
        sourceId: source.id,
        renderedId: rendered.id,
        coordinates,
      }),
    });
    crop.value = null;
    window.loadNotification?.({ message: `${slot.title} set. Save the page to make it live.` });
  } catch (error) {
    // The render failed after the original went up: nothing will ever refer
    // to that original, so it is the one case worth deleting right away.
    if (uploadedOriginal) {
      inFlight.delete(uploadedOriginal.id);
      await deleteMedia(uploadedOriginal.id);
    }
    uploadFailed(error);
  } finally {
    busy.value = null;
  }
}

function uploadFailed(error) {
  window.loadNotification?.({
    message: error?.response?.data?.message || error.message || 'The upload failed. Nothing was changed.',
    error: true,
  });
}

/**
 * The record keeps the upload's id with no render and no coordinates: the
 * server tidies it like any other once a later save supersedes it, and the
 * card reads "nothing to resume" as "no Crop button".
 */
async function applyAsIs(slot, file) {
  if (busy.value) return;
  busy.value = slot.key;
  try {
    const uploaded = await upload(file, file.name);
    if (tornDown) { await deleteMedia(uploaded.id); return; }
    if (uploaded.id) pending.add(uploaded.id);
    patch({
      [slot.key]: uploaded.url,
      [slot.cropKey]: JSON.stringify({ source: uploaded.url, sourceId: uploaded.id, renderedId: null, coordinates: null }),
    });
    window.loadNotification?.({ message: `${slot.title} set. Save the page to make it live.` });
  } catch (error) {
    uploadFailed(error);
  } finally {
    busy.value = null;
  }
}

// Closing the crop while its uploads are in flight is ignored: the save is
// already happening, and a modal that vanished mid-way would look like a
// cancel that was not one.
function closeCrop() {
  if (!busy.value) crop.value = null;
}

/* ---- removing ---- */
const removing = ref(null); // slot

function confirmRemove() {
  const slot = removing.value;
  patch({ [slot.key]: '', [slot.cropKey]: '' });
  removing.value = null;
  window.loadNotification?.({ message: `${slot.title} removed. Save the page to make it live.` });
}
</script>

<template>
  <XlCard padded class="logo-card">
    <div class="logo-card__head">
      <h2>Logo</h2>
      <p>Two files, because the sidebar has two widths. The full logo also appears on invitation emails and at the top of your hub's sign-in page; the collapsed one is only for the narrow sidebar.</p>
    </div>

    <div
      v-for="slot in SLOTS"
      :key="slot.key"
      class="logo-slot"
      :class="[`logo-slot--${slot.shape}`, { 'logo-slot--filled': has(slot.key) }]"
      :data-testid="`logo-slot-${slot.key}`"
    >
      <div
        class="logo-slot__frame"
        :class="`logo-slot__frame--${slot.shape}`"
      >
        <img v-if="has(slot.key)" :src="value(slot.key)" :alt="slot.title" />
        <span v-else class="logo-slot__dims">{{ SLOT_SHAPES[slot.shape].width }} × {{ SLOT_SHAPES[slot.shape].height }}</span>
      </div>

      <div class="logo-slot__text">
        <b>{{ slot.title }}</b>
        <span>{{ slot.sub }}</span>
        <small>{{ slot.spec }}</small>
      </div>

      <div class="logo-slot__actions">
        <template v-if="has(slot.key)">
          <!-- Every slot waits while any upload runs: the crop modal is one
               shared piece of state, and a second slot opening it mid-upload
               would have the first upload close it. -->
          <XlButton variant="ghost" size="sm" :disabled="!!busy" @click="pick(slot)">Replace</XlButton>
          <XlButton v-if="cropOffered(slot)" variant="ghost" size="sm" :disabled="!!busy" @click="recrop(slot)">Crop</XlButton>
          <XlButton variant="ghost-danger" size="sm" :disabled="!!busy" @click="removing = slot">Remove</XlButton>
        </template>
        <XlButton v-else variant="ghost" size="sm" :loading="busy === slot.key" :disabled="!!busy" @click="pick(slot)">Upload</XlButton>
      </div>

      <input
        :ref="(el) => { pickers[slot.key] = el; }"
        type="file"
        accept=".svg,.png,image/svg+xml,image/png"
        class="logo-slot__picker"
        :aria-label="`Choose a file for the ${slot.title.toLowerCase()}`"
        @change="onPicked(slot, $event)"
      />
    </div>

    <!-- How it sits in the sidebar: the two widths, drawn from the same rules
         HubBrand uses, so what the owner sees here is what the rail will do. -->
    <div class="logo-sidebar">
      <b>How it sits in the sidebar</b>
      <div class="logo-sidebar__pair">
        <div class="logo-sidebar__open" data-testid="logo-preview-open">
          <div v-if="has('logo')" class="logo-sidebar__slot"><img :src="value('logo')" alt="" /></div>
          <span class="logo-sidebar__name">{{ hubName || 'Your hub' }}</span>
          <span class="logo-sidebar__role">Agency Hub · Owner</span>
          <small>open</small>
        </div>

        <div class="logo-sidebar__rail" data-testid="logo-preview-collapsed">
          <img v-if="has('logoMark')" :src="value('logoMark')" alt="" class="logo-sidebar__mark" />
          <img v-else-if="has('logo')" :src="value('logo')" alt="" class="logo-sidebar__scaled" />
          <span v-else class="logo-sidebar__letter">{{ initial }}</span>
          <small>collapsed</small>
        </div>
      </div>

      <p class="logo-sidebar__status" :class="{ 'logo-sidebar__status--warn': status.warn }" data-testid="logo-status">
        {{ status.text }}
      </p>
    </div>

    <LogoCropModal
      v-if="crop"
      :src="crop.src"
      :shape="crop.slot.shape"
      :coordinates="crop.coordinates"
      :title="`Crop the ${crop.slot.title.toLowerCase()}`"
      :saving="busy === crop.slot.key"
      @close="closeCrop"
      @save="onCropSaved"
    />

    <!-- Remove asks first, and names what takes the logo's place (AC2). -->
    <XlModal v-if="removing" :title="`Remove the ${removing.title.toLowerCase()}?`" @close="removing = null">
      <p>{{ removing.fallback }} You can upload another at any time.</p>
      <template #actions>
        <XlButton variant="ghost" @click="removing = null">Cancel</XlButton>
        <XlButton variant="danger" data-testid="logo-remove-confirm" @click="confirmRemove">Remove</XlButton>
      </template>
    </XlModal>
  </XlCard>
</template>

<style scoped>
.logo-card, .logo-card * { box-sizing: border-box; }

.logo-card__head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}

.logo-card__head h2 {
  margin: 0;
  font-size: var(--xl-type-body-large-size);
  line-height: var(--xl-type-body-large-line);
  font-weight: 700;
  color: var(--xl-color-primary-text);
}

.logo-card__head p {
  margin: 0;
  font-size: var(--xl-type-body-medium-size);
  line-height: var(--xl-type-body-medium-line);
  color: var(--xl-color-secondary-text);
}

/* A grid, not a wrapping row (XN-4579). The design shows the wide frame on
   a row of its own above its text and the square frame beside its text,
   with the buttons under both — at every width, not only the one where a
   flex row happened to wrap that way. */
.logo-slot {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  margin-bottom: 12px;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  background: #fff;
}

.logo-slot--wide { grid-template-columns: minmax(0, 1fr); }
.logo-slot--wide .logo-slot__frame { justify-self: start; }

.logo-slot--filled { border-color: var(--xl-color-primary-fixed-dim); }

.logo-slot__frame {
  display: grid;
  place-items: center;
  height: 56px;
  padding: 6px;
  border: 1px dashed var(--xl-color-border);
  border-radius: 10px;
  background: #fff;
}

.logo-slot--filled .logo-slot__frame { border-color: var(--xl-color-primary-fixed-dim); }
.logo-slot__frame--wide { width: 172px; }
.logo-slot__frame--square { width: 56px; }
.logo-slot__frame img { max-width: 100%; max-height: 100%; object-fit: contain; }

.logo-slot__dims {
  font-size: 11.5px;
  line-height: 1.3;
  text-align: center;
  color: var(--xl-color-outline);
}

.logo-slot__text {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.logo-slot__text b { font-size: var(--xl-type-body-medium-size); color: var(--xl-color-primary-text); }
.logo-slot__text span { margin-top: 2px; font-size: 12.5px; color: var(--xl-color-secondary-text); }
.logo-slot__text small { margin-top: 4px; font-size: var(--xl-type-body-small-size); color: var(--xl-color-outline); }

.logo-slot__actions { grid-column: 1 / -1; display: flex; gap: 14px; flex-wrap: wrap; }

.logo-slot__picker {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}

.logo-sidebar {
  margin-top: 4px;
  padding: 14px 16px;
  border-radius: var(--xl-radius-lg);
  background: var(--xl-color-background, #fcfafd);
}

.logo-sidebar > b { font-size: 13.5px; color: var(--xl-color-primary-text); }

.logo-sidebar__pair {
  display: flex;
  gap: 14px;
  margin-top: 12px;
  flex-wrap: wrap;
  align-items: flex-start;
}

.logo-sidebar__open,
.logo-sidebar__rail {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--xl-color-border);
  border-radius: 12px;
  background: #fff;
}

.logo-sidebar__open { width: 206px; padding: 12px 10px; }
.logo-sidebar__rail { width: 74px; padding: 12px 8px; align-items: center; text-align: center; }

/* The design's preview draws the open logo at 38px and the mark and the
   letter at 34px — a few px under what HubBrand really uses (xl-tokens),
   and what QA measures against (XN-4579). The scaled wordmark keeps the
   rail's 52 × 18. */
.logo-sidebar__slot { height: 38px; margin-bottom: 10px; }
.logo-sidebar__slot img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }

.logo-sidebar__name {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-size: 13.5px;
  font-weight: 700;
  line-height: 1.3;
  color: var(--xl-color-primary-text);
}

.logo-sidebar__role {
  margin-top: 2px;
  font-size: 11px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--xl-color-secondary-text);
}

.logo-sidebar__mark { width: 34px; height: 34px; object-fit: contain; }
.logo-sidebar__scaled { width: var(--xl-brand-scaled-width, 52px); height: var(--xl-brand-scaled-height, 18px); object-fit: contain; }

.logo-sidebar__letter {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: var(--xl-color-primary);
  color: var(--xl-color-on-primary);
  font-size: 14px;
  font-weight: 700;
}

.logo-sidebar__open small,
.logo-sidebar__rail small {
  margin-top: 10px;
  font-size: 10.5px;
  color: var(--xl-color-outline);
}

.logo-sidebar__status {
  margin: 12px 0 0;
  font-size: 12.5px;
  line-height: 1.6;
  color: var(--xl-color-secondary-text);
}

.logo-sidebar__status--warn { color: var(--xl-color-warning); }
</style>
